import { Button } from '@shared/components/ui/button';
import { DatePicker, ExpiryDatePicker } from '@shared/components/ui/date-picker';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { ProductCombobox } from '@shared/components/ui/ProductCombobox';
import { useToast } from '@shared/hooks/use-toast';
import { useBatchProductSearch } from '../../../hooks/useBatchProductSearch';
import { format } from 'date-fns';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateBatch } from '../../../hooks/useBatchMutations';

const CreateBatchModal = forwardRef<ModalCenterRef, object>((_, ref) => {
  const { t } = useTranslation('batchesModule');
  const { toast } = useToast();
  const createBatch = useCreateBatch();
  const internalModalRef = useRef<ModalCenterRef>(null);

  const { searchProductsForCombobox } = useBatchProductSearch();

  const [productId, setProductId] = useState('');
  const [batchNumber, setBatchNumber] = useState('');
  const [receivedAtDate, setReceivedAtDate] = useState<Date | undefined>(undefined);
  const [expiryDate, setExpiryDate] = useState<Date | undefined>(undefined);
  const [supplierReference, setSupplierReference] = useState('');

  const resetForm = () => {
    setProductId('');
    setBatchNumber('');
    setReceivedAtDate(undefined);
    setExpiryDate(undefined);
    setSupplierReference('');
  };

  const handleCancel = () => {
    internalModalRef.current?.closeModal();
    resetForm();
  };

  const handleSubmit = async () => {
    if (!productId || !batchNumber.trim() || !receivedAtDate) {
      toast({
        title: t('toast.createError'),
        description: t('modals.create.batch_number'),
        variant: 'destructive',
      });
      return;
    }

    try {
      await createBatch.mutateAsync({
        product_id: parseInt(productId, 10),
        batch_number: batchNumber.trim(),
        received_at: format(receivedAtDate!, 'yyyy-MM-dd'),
        expiry_date: expiryDate ? format(expiryDate, 'yyyy-MM-dd') : null,
        supplier_reference: supplierReference.trim() || null,
      });

      toast({ title: t('toast.createSuccess'), variant: 'default' });
      handleCancel();
    } catch (error: any) {
      toast({
        title: t('toast.createError'),
        description: error?.response?.data?.message || error.message,
        variant: 'destructive',
      });
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      openModal: () => internalModalRef.current?.openModal(),
      closeModal: () => internalModalRef.current?.closeModal(),
    }),
    [],
  );

  return (
    <ModalCenter ref={internalModalRef} title={t('modals.create.title')} type="info">
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-text">{t('modals.create.product')} *</label>
          <ProductCombobox
            value={productId}
            setValue={setProductId}
            placeholder={t('modals.create.product_placeholder')}
            searchFn={searchProductsForCombobox}
            className="w-full"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-text">{t('modals.create.batch_number')} *</label>
          <Input
            value={batchNumber}
            onChange={(e) => setBatchNumber(e.target.value)}
            placeholder={t('modals.create.batch_number_placeholder')}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-text">{t('modals.create.received_at')} *</label>
          <DatePicker
            date={receivedAtDate}
            onDateChange={setReceivedAtDate}
            placeholder={t('modals.create.date_placeholder')}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-text">{t('modals.create.expiry_date')}</label>
          <ExpiryDatePicker
            date={expiryDate}
            onDateChange={setExpiryDate}
            fromYear={new Date().getFullYear()}
            toYear={new Date().getFullYear() + 20}
            labels={{
              day: t('modals.create.expiry_day'),
              month: t('modals.create.expiry_month'),
              year: t('modals.create.expiry_year'),
              preview: t('modals.create.expiry_preview'),
              search: t('modals.create.expiry_search'),
              notFound: t('modals.create.expiry_not_found'),
            }}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-text">{t('modals.create.supplier_reference')}</label>
          <Input
            value={supplierReference}
            onChange={(e) => setSupplierReference(e.target.value)}
            placeholder={t('modals.create.supplier_reference_placeholder')}
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="lg" onClick={handleCancel} disabled={createBatch.isPending}>
            {t('modals.create.cancel')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            className="text-white"
            onClick={handleSubmit}
            disabled={createBatch.isPending || !productId || !batchNumber.trim() || !receivedAtDate}
          >
            {t('modals.create.submit')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

CreateBatchModal.displayName = 'CreateBatchModal';

export default CreateBatchModal;
